import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  listSpaces,
  getSpaceContent,
  getContent,
  streamChat,
  createChatThread,
  createSpace,
  updateSpace,
  deleteSpace,
  renameContent,
  deleteContent,
  moveContentToSpace,
  searchContents,
  listContents,
  listChatThreads,
  deleteChatThread,
  renameChatThread,
  reimportContent,
  shareContent,
  createContent,
  getCurrentUser,
} from '../lib/api'
import { formatDate, contentTypeConfig } from '../lib/utils'
import { getIdToken } from '../lib/firebase'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  Folder,
  FileText,
  Globe,
  Image as ImageIcon,
  Mic,
  BookOpen,
  File,
  Loader2,
  MessageSquare,
  Send,
  PanelLeftClose,
  PanelRightClose,
  Search,
  Plus,
  MoreHorizontal,
  X,
  Bot,
  User,
  Sparkles,
  Columns,
  Square,
  Maximize2,
  Paperclip,
  Pencil,
  Trash2,
  FolderInput,
  FolderPlus,
  Filter,
  Calendar,
  Clock,
  History,
  Settings,
  ChevronLeft,
  Eye,
  Volume2,
  Play,
  Pause,
  Code,
  Download,
  ExternalLink,
  Share2,
  RefreshCw,
  Info,
  RotateCcw,
  Check,
  Copy,
  FilePlus2,
  FolderUp,
  ChevronUp,
  Crown,
} from 'lucide-react'
import { AttachedContents, ContentPickerModal } from '../components/ContentPicker'
import { ImportContentModal } from '../components/ImportContentModal'
import { BulkImportModal } from '../components/BulkImportModal'
import { 
  RenameDialog, 
  ConfirmDialog, 
  MoveToSpaceDialog, 
  CreateSpaceDialog 
} from '../components/ContextMenu'

const iconMap = {
  note: FileText,
  bookmark: Globe,
  image: ImageIcon,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xlsx: FileText,
  xls: FileText,
  pptx: FileText,
  ppt: FileText,
  txt: FileText,
  epub: BookOpen,
  transcript: Mic,
  unknown: File,
}

export default function KnowledgeBasePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Layout state
  const [layoutMode, setLayoutMode] = useState('master-detail') // 'master-detail' | 'detail-only' | 'focus'
  const [showMaster, setShowMaster] = useState(true)
  const [showChat, setShowChat] = useState(true)
  
  // Search state
  const [searchMode, setSearchMode] = useState('search') // 'search' | 'filter'
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchFilters, setSearchFilters] = useState({
    type: '',
    spaceId: '',
    dateFrom: '',
    dateTo: '',
  })

  // Tree state
  const [spaces, setSpaces] = useState([])
  const [contents, setContents] = useState([])
  const [expandedSpaces, setExpandedSpaces] = useState({})
  const [spaceContents, setSpaceContents] = useState({})
  const [treeLoading, setTreeLoading] = useState(true)
  
  // Selected content state
  const [selectedContentId, setSelectedContentId] = useState(searchParams.get('content'))
  const [selectedContent, setSelectedContent] = useState(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [contentViewMode, setContentViewMode] = useState('view') // 'view' | 'markdown' | 'listen'
  const [showContentMenu, setShowContentMenu] = useState(false)
  
  // Chat state
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [threadId, setThreadId] = useState(null)
  const [attachedContents, setAttachedContents] = useState([])
  const [showContentPicker, setShowContentPicker] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showBulkImportModal, setShowBulkImportModal] = useState(false)
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [rootSpaceId, setRootSpaceId] = useState(null)
  const [isPremium, setIsPremium] = useState(false)
  
  // Chat history state
  const [chatThreads, setChatThreads] = useState([])
  const [showChatHistory, setShowChatHistory] = useState(false)
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false)

  // Dialog states for content/space management
  const [renameDialog, setRenameDialog] = useState({ open: false, type: null, id: null, name: '' })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' })
  const [moveDialog, setMoveDialog] = useState({ open: false, contentId: null, currentSpaceId: null })
  const [createSpaceDialog, setCreateSpaceDialog] = useState({ open: false, parentId: null })
  const [allSpaces, setAllSpaces] = useState([]) // Flat list of all spaces for move dialog
  const [contentInfoDialog, setContentInfoDialog] = useState({ open: false, content: null })

  // Load tree and user data on mount
  useEffect(() => {
    loadTree()
    loadUserData()
  }, [])

  // Check for import URL parameter (triggered from sidebar "New Content" button)
  useEffect(() => {
    if (searchParams.get('import') === 'true') {
      setShowImportModal(true)
      // Remove the import param from URL without triggering navigation
      searchParams.delete('import')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams])

  const loadUserData = async () => {
    try {
      const response = await getCurrentUser()
      const userData = response.user || response.data?.user || response
      const tier = userData?.tier || ''
      const paymentProvider = userData?.payment_provider || null

      // Check if user is premium
      const isPremiumTier = tier && tier !== 'free' && (
        tier.includes('premium') ||
        tier.includes('pro') ||
        tier.startsWith('price_') ||
        tier.startsWith('app.') ||
        tier.startsWith('com.') ||
        paymentProvider !== null
      )
      setIsPremium(isPremiumTier)
    } catch (err) {
      console.error('Failed to load user data:', err)
    }
  }

  // Load content when selection changes
  useEffect(() => {
    if (selectedContentId) {
      loadContent(selectedContentId)
      setSearchParams({ content: selectedContentId })
    } else {
      setSelectedContent(null)
      setSearchParams({})
    }
  }, [selectedContentId])

  const loadTree = async () => {
    setTreeLoading(true)
    try {
      const res = await listSpaces()
      console.log('Tree data:', res)
      setSpaces(res.spaces || [])
      setContents(res.contents || [])
      if (res.parentId) {
        setRootSpaceId(res.parentId.toString())
      }
    } catch (err) {
      console.error('Failed to load tree:', err)
    } finally {
      setTreeLoading(false)
    }
  }

  const loadSpaceContent = async (spaceId) => {
    if (spaceContents[spaceId]) return
    try {
      const res = await getSpaceContent(spaceId)
      setSpaceContents(prev => ({
        ...prev,
        [spaceId]: {
          spaces: res.spaces || [],
          contents: res.contents || [],
        }
      }))
    } catch (err) {
      console.error('Failed to load space content:', err)
    }
  }

  const toggleSpace = async (spaceId) => {
    const isExpanded = expandedSpaces[spaceId]
    setExpandedSpaces(prev => ({ ...prev, [spaceId]: !isExpanded }))
    if (!isExpanded) {
      await loadSpaceContent(spaceId)
    }
  }

  // Helper function to decode base64 with UTF-8 support
  const decodeBase64UTF8 = (base64String) => {
    try {
      // Decode base64 to binary string
      const binaryString = atob(base64String)
      // Convert binary string to Uint8Array
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      // Decode UTF-8 bytes to string
      return new TextDecoder('utf-8').decode(bytes)
    } catch (e) {
      console.error('Failed to decode base64 UTF-8:', e)
      // Fallback to simple atob
      return atob(base64String)
    }
  }

  const loadContent = async (contentId) => {
    setContentLoading(true)
    setMessages([])
    setThreadId(null)
    try {
      const res = await getContent(contentId)
      const contentData = res.metadata || res
      if (res.content && res.contentEncoding === 'base64') {
        try {
          contentData.content_text = decodeBase64UTF8(res.content)
        } catch (e) {
          console.error('Failed to decode base64:', e)
        }
      }
      setSelectedContent(contentData)
    } catch (err) {
      console.error('Failed to load content:', err)
      setSelectedContent({
        title: 'Unable to load content',
        type: 'note',
        content_text: `This content could not be loaded. It may have been deleted or is temporarily unavailable.\n\nContent ID: ${contentId}`
      })
    } finally {
      setContentLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!inputMessage.trim() || chatLoading) return
    
    const userMessage = inputMessage.trim()
    setInputMessage('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setChatLoading(true)
    
    try {
      let currentThreadId = threadId
      if (!currentThreadId) {
        // Create thread with content IDs - include selected content and any attached contents
        const allContentIds = [
          ...(selectedContentId ? [selectedContentId] : []),
          ...attachedContents.map(c => c.content_id || c.id)
        ]
        const thread = await createChatThread({
          name: selectedContent?.title || 'Chat',
          contentIds: allContentIds,
          question: userMessage,
        })
        currentThreadId = thread.threadId || thread.thread_id
        setThreadId(currentThreadId)
      }

      let assistantMessage = ''
      setMessages(prev => [...prev, { role: 'assistant', content: '' }])

      // streamChat is a generator function
      for await (const data of streamChat(currentThreadId, userMessage)) {
        if (data.type === 'token' && data.token) {
          assistantMessage += data.token
          setMessages(prev => {
            const newMessages = [...prev]
            newMessages[newMessages.length - 1] = { role: 'assistant', content: assistantMessage }
            return newMessages
          })
        } else if (data.type === 'done' && data.response && !assistantMessage) {
          assistantMessage = data.response
          setMessages(prev => {
            const newMessages = [...prev]
            newMessages[newMessages.length - 1] = { role: 'assistant', content: assistantMessage }
            return newMessages
          })
        }
      }
    } catch (err) {
      console.error('Chat error:', err)
      setMessages(prev => [...prev.slice(0, -1), { role: 'assistant', content: 'Sorry, an error occurred.' }])
    } finally {
      setChatLoading(false)
    }
  }

  const handleContentSelect = (contents) => {
    setAttachedContents(contents)
  }

  const handleRemoveContent = (content) => {
    const id = content.content_id || content.id
    setAttachedContents(prev => prev.filter(c => (c.content_id || c.id) !== id))
  }

  // Build flat list of all spaces for move dialog
  const buildAllSpaces = () => {
    const result = []
    const addSpaces = (spaceList, prefix = '') => {
      for (const space of spaceList) {
        result.push({ ...space, displayName: prefix + space.name })
        if (spaceContents[space.id]?.spaces) {
          addSpaces(spaceContents[space.id].spaces, prefix + space.name + ' / ')
        }
      }
    }
    // Add root space option
    if (rootSpaceId) {
      result.push({ id: parseInt(rootSpaceId), name: 'Root', displayName: 'Root (Home)' })
    }
    addSpaces(spaces)
    return result
  }

  // Content management handlers
  const handleRenameContent = async (newName) => {
    if (!renameDialog.id) return
    await renameContent(renameDialog.id, newName)
    // Refresh tree and content
    await loadTree()
    setSpaceContents({})
    if (selectedContentId === renameDialog.id) {
      await loadContent(selectedContentId)
    }
  }

  const handleDeleteContent = async () => {
    if (!deleteDialog.id) return
    await deleteContent(deleteDialog.id)
    // Clear selection if deleted content was selected
    if (selectedContentId === deleteDialog.id) {
      setSelectedContentId(null)
    }
    // Refresh tree
    await loadTree()
    setSpaceContents({})
  }

  const handleMoveContent = async (newSpaceId) => {
    if (!moveDialog.contentId) return
    await moveContentToSpace(moveDialog.contentId, newSpaceId)
    // Refresh tree
    await loadTree()
    setSpaceContents({})
  }

  // Space management handlers
  const handleCreateSpace = async (name, parentId) => {
    await createSpace(name, parentId || rootSpaceId)
    await loadTree()
    setSpaceContents({})
  }

  const handleRenameSpace = async (newName) => {
    if (!renameDialog.id) return
    await updateSpace(renameDialog.id, { name: newName })
    await loadTree()
    setSpaceContents({})
  }

  const handleDeleteSpace = async () => {
    if (!deleteDialog.id) return
    await deleteSpace(deleteDialog.id)
    await loadTree()
    setSpaceContents({})
  }

  // Open dialogs
  const openRenameContentDialog = (content) => {
    setRenameDialog({
      open: true,
      type: 'content',
      id: content.content_id || content.id,
      name: content.title
    })
  }

  const openDeleteContentDialog = (content) => {
    setDeleteDialog({
      open: true,
      type: 'content',
      id: content.content_id || content.id,
      name: content.title
    })
  }

  const openMoveContentDialog = (content) => {
    setAllSpaces(buildAllSpaces())
    setMoveDialog({
      open: true,
      contentId: content.content_id || content.id,
      currentSpaceId: content.space_id
    })
  }

  const openRenameSpaceDialog = (space) => {
    setRenameDialog({
      open: true,
      type: 'space',
      id: space.id,
      name: space.name
    })
  }

  const openDeleteSpaceDialog = (space) => {
    setDeleteDialog({
      open: true,
      type: 'space',
      id: space.id,
      name: space.name
    })
  }

  const openCreateSpaceDialog = (parentId = null) => {
    setCreateSpaceDialog({ open: true, parentId })
  }

  // Search functions
  const handleSearch = async (query) => {
    if (!query.trim() && searchMode === 'search') return
    setSearchLoading(true)
    try {
      if (searchMode === 'filter') {
        // Filter mode: use listContents API with type and space filters
        const filters = {}
        if (searchFilters.type) {
          filters.types = [searchFilters.type]
        }
        if (searchFilters.spaceId) {
          // "root" means items without a space (space_id = 0)
          filters.spaceId = searchFilters.spaceId === 'root' ? '0' : searchFilters.spaceId
        }
        // Need at least one filter
        if (!filters.types && !filters.spaceId) {
          setSearchResults([])
          return
        }
        const res = await listContents(filters)
        // Convert content items to search result format
        const contents = res.contents || res.data || res || []
        const results = (Array.isArray(contents) ? contents : []).map(content => ({
          metadata: {
            content_id: content.content_id || content.id,
            title: content.title,
            type: content.type,
            description: content.description,
            created_at: content.created_at,
            updated_at: content.updated_at,
          },
          ...content,
        }))
        setSearchResults(results)
      } else {
        // Search mode: use searchContents API
        const res = await searchContents(query, {})
        // Try different response structures
        const matches = res.results?.matches || res.matches || res.results || res.data || []
        // Deduplicate by content_id (same content may have multiple chunk matches)
        const seen = new Set()
        const results = (Array.isArray(matches) ? matches : []).filter(match => {
          const contentId = match.metadata?.content_id || match.content_id || match.contentId
          if (!contentId || seen.has(contentId)) return false
          seen.add(contentId)
          return true
        })
        setSearchResults(results)
      }
    } catch (err) {
      console.error('Search error:', err)
      setSearchResults([])
    } finally {
      setSearchLoading(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults([])
    setSearchFilters({ type: '', spaceId: '', dateFrom: '', dateTo: '' })
  }

  // Chat history functions
  const loadChatHistory = async () => {
    setChatHistoryLoading(true)
    try {
      const res = await listChatThreads()
      setChatThreads(res.threads || [])
    } catch (err) {
      console.error('Failed to load chat history:', err)
    } finally {
      setChatHistoryLoading(false)
    }
  }

  const handleDeleteThread = async (threadIdToDelete) => {
    try {
      await deleteChatThread(threadIdToDelete)
      setChatThreads(prev => prev.filter(t => t.id !== threadIdToDelete))
      if (threadId === threadIdToDelete) {
        setThreadId(null)
        setMessages([])
      }
    } catch (err) {
      console.error('Failed to delete thread:', err)
    }
  }

  const handleRenameThread = async (threadIdToRename, newName) => {
    try {
      await renameChatThread(threadIdToRename, newName)
      setChatThreads(prev => prev.map(t => 
        t.id === threadIdToRename ? { ...t, name: newName } : t
      ))
    } catch (err) {
      console.error('Failed to rename thread:', err)
    }
  }

  const loadThread = async (thread) => {
    setThreadId(thread.id)
    setMessages(thread.messages || [])
    setShowChatHistory(false)
  }

  // Content actions
  const handleReimport = async () => {
    if (!selectedContentId) return
    try {
      await reimportContent(selectedContentId)
      await loadContent(selectedContentId)
    } catch (err) {
      console.error('Reimport error:', err)
    }
  }

  const handleShare = async () => {
    if (!selectedContentId) return
    try {
      await shareContent(selectedContentId)
    } catch (err) {
      console.error('Share error:', err)
    }
  }

  const handleExport = async () => {
    if (!selectedContent) return
    // For bookmark, open URL; for others, download
    if (selectedContent.type === 'bookmark' && selectedContent.url) {
      window.open(selectedContent.url, '_blank')
    } else {
      // Download content
      const token = await getIdToken()
      const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'
      window.open(`${API_BASE}/content/${selectedContentId}?raw=true&token=${token}`, '_blank')
    }
  }

  const openContentInfo = () => {
    setContentInfoDialog({ open: true, content: selectedContent })
  }

  return (
    <div className="flex-1 flex flex-col bg-background overflow-hidden">
      {/* Workspace Toolbar */}
      <div className="h-14 flex-shrink-0 border-b border-border/50 flex items-center justify-between px-4 lg:px-6 glass-subtle">
        <div className="flex items-center gap-3">
          <h1 className="font-bold text-lg flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              Workspace
            </h1>

          {/* Layout Mode Toggle */}
          <div className="hidden sm:flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border/50">
            <button
              onClick={() => {
                setLayoutMode('master-detail')
                setShowMaster(true)
                setShowChat(true)
              }}
              className={`p-2 rounded-lg transition-all duration-200 ${layoutMode === 'master-detail' ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
              title="Master-Detail-Chat (3 panels)"
            >
              <Columns className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setLayoutMode('detail-only')
                setShowMaster(false)
                setShowChat(true)
              }}
              className={`p-2 rounded-lg transition-all duration-200 ${layoutMode === 'detail-only' ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
              title="Detail + Chat (2 panels)"
            >
              <Square className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setLayoutMode('focus')
                setShowMaster(false)
                setShowChat(false)
              }}
              className={`p-2 rounded-lg transition-all duration-200 ${layoutMode === 'focus' ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
              title="Focus Mode (content only)"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Individual Panel Toggles */}
          {layoutMode === 'master-detail' && (
            <button
              onClick={() => setShowMaster(!showMaster)}
              className={`p-2 rounded-lg transition-colors ${showMaster ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
              title={showMaster ? 'Hide sidebar' : 'Show sidebar'}
            >
              <PanelLeftClose className={`w-4 h-4 ${!showMaster ? 'rotate-180' : ''}`} />
            </button>
          )}

          {layoutMode !== 'focus' && (
            <button
              onClick={() => setShowChat(!showChat)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${showChat ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
              title={showChat ? 'Hide chat' : 'Show chat'}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Chat</span>
            </button>
          )}

          {/* Add Content Button with Dropdown for Premium */}
          <div className="relative">
            {isPremium ? (
              <>
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="btn-primary text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Content</span>
                  <ChevronUp className={`w-3.5 h-3.5 transition-transform ${showAddMenu ? '' : 'rotate-180'}`} />
                </button>

                {showAddMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowAddMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-52 bg-card rounded-xl border border-border shadow-lg z-50 overflow-hidden">
                      <button
                        onClick={() => {
                          setShowAddMenu(false)
                          setShowImportModal(true)
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
                      >
                        <Plus className="w-4 h-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">Add Single</p>
                          <p className="text-xs text-muted-foreground">Import one file or URL</p>
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setShowAddMenu(false)
                          setShowBulkImportModal(true)
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors text-left border-t border-border"
                      >
                        <FolderUp className="w-4 h-4 text-amber-500" />
                        <div>
                          <p className="text-sm font-medium flex items-center gap-1.5">
                            Bulk Import
                            <Crown className="w-3 h-3 text-amber-500" />
                          </p>
                          <p className="text-xs text-muted-foreground">Import folder or multiple files</p>
                        </div>
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <button
                onClick={() => setShowImportModal(true)}
                className="btn-primary text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Content</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Master Panel - Tree View */}
        {showMaster && (
          <div className="w-72 lg:w-80 border-r border-border/50 flex flex-col glass-subtle">
            <div className="p-4 border-b border-border/50 space-y-3">
              {/* Search/Filter Toggle */}
              <div className="flex items-center justify-center">
                <div className="flex items-center p-1 rounded-xl bg-muted/50 border border-border/50">
                  <button
                    onClick={() => setSearchMode('search')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      searchMode === 'search'
                        ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Search className="w-3.5 h-3.5" />
                    Search
                  </button>
                  <button
                    onClick={() => setSearchMode('filter')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      searchMode === 'filter'
                        ? 'bg-primary text-primary-foreground shadow-md shadow-primary/25'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Filter className="w-3.5 h-3.5" />
                    Filter
                  </button>
                </div>
              </div>

              {/* Search Input */}
              {searchMode === 'search' && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                    placeholder="Search content..."
                    className="input-modern pl-10 pr-8 py-2.5 text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-accent"
                    >
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}

              {/* Filter Options */}
              {searchMode === 'filter' && (
                <div className="space-y-2">
                  <select
                    value={searchFilters.type}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, type: e.target.value }))}
                    className="input-modern py-2.5 text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="note">Notes</option>
                    <option value="bookmark">Bookmarks</option>
                    <option value="pdf">PDFs</option>
                    <option value="docx">Word Docs</option>
                    <option value="pptx">PowerPoints</option>
                    <option value="xlsx">Excel</option>
                    <option value="epub">eBooks</option>
                    <option value="image">Images</option>
                    <option value="transcript">Audio</option>
                  </select>
                  <select
                    value={searchFilters.spaceId}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, spaceId: e.target.value }))}
                    className="input-modern py-2.5 text-sm"
                  >
                    <option value="">All Spaces</option>
                    <option value="root">Root (No Space)</option>
                    {spaces.map(space => (
                      <option key={space.id} value={space.id}>{space.name}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={searchFilters.dateFrom}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="flex-1 px-2 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="From"
                    />
                    <input
                      type="date"
                      value={searchFilters.dateTo}
                      onChange={(e) => setSearchFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="flex-1 px-2 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="To"
                    />
                  </div>
                  <button
                    onClick={() => handleSearch('')}
                    className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              )}

              {/* New Space Button */}
              <button
                onClick={() => openCreateSpaceDialog(null)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-border/80 hover:border-primary/50 hover:bg-primary/5 transition-all text-sm text-muted-foreground hover:text-primary"
              >
                <FolderPlus className="w-4 h-4" />
                New Space
              </button>
            </div>
            <div className="flex-1 overflow-auto p-2">
              {searchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-xs text-muted-foreground">{searchResults.length} results</span>
                    <button onClick={clearSearch} className="text-xs text-primary hover:underline">Clear</button>
                  </div>
                  {searchResults.map((result, idx) => {
                    const content = result.metadata || result
                    return (
                      <ContentNode
                        key={content.contentId || content.content_id || content.id || idx}
                        content={{
                          ...content,
                          content_id: content.contentId || content.content_id || content.id,
                          title: content.title,
                          type: content.type,
                        }}
                        selected={selectedContentId === (content.contentId || content.content_id || content.id)}
                        onSelect={() => setSelectedContentId(content.contentId || content.content_id || content.id)}
                        onRename={() => openRenameContentDialog(content)}
                        onDelete={() => openDeleteContentDialog(content)}
                        onMove={() => openMoveContentDialog(content)}
                      />
                    )
                  })}
                </div>
              ) : treeLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <TreeView
                  spaces={spaces}
                  contents={contents}
                  expandedSpaces={expandedSpaces}
                  spaceContents={spaceContents}
                  selectedContentId={selectedContentId}
                  onToggleSpace={toggleSpace}
                  onSelectContent={setSelectedContentId}
                  onRenameSpace={openRenameSpaceDialog}
                  onDeleteSpace={openDeleteSpaceDialog}
                  onCreateSpace={openCreateSpaceDialog}
                  onRenameContent={openRenameContentDialog}
                  onDeleteContent={openDeleteContentDialog}
                  onMoveContent={openMoveContentDialog}
                />
              )}
            </div>
          </div>
        )}

        {/* Detail Panel - Content Viewer */}
        <div className="flex-1 flex flex-col overflow-hidden bg-background">
          {contentLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : selectedContent ? (
            <>
              {/* Content Header with Actions */}
              <div className="h-12 border-b border-border flex items-center justify-between px-4 bg-card/50">
                <div className="flex items-center gap-3 min-w-0">
                  <h2 className="font-medium text-sm truncate">{selectedContent.title}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {contentTypeConfig[selectedContent.type]?.label || selectedContent.type}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {/* View Mode Toggle */}
                  <div className="flex items-center p-0.5 rounded-lg bg-muted mr-2">
                    <button
                      onClick={() => setContentViewMode('view')}
                      className={`p-1.5 rounded-md transition-all ${
                        contentViewMode === 'view' ? 'bg-background shadow-sm' : 'hover:bg-background/50'
                      }`}
                      title="View"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setContentViewMode('markdown')}
                      className={`p-1.5 rounded-md transition-all ${
                        contentViewMode === 'markdown' ? 'bg-background shadow-sm' : 'hover:bg-background/50'
                      }`}
                      title="Markdown"
                    >
                      <Code className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setContentViewMode('listen')}
                      className={`p-1.5 rounded-md transition-all ${
                        contentViewMode === 'listen' ? 'bg-background shadow-sm' : 'hover:bg-background/50'
                      }`}
                      title="Listen"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Action Buttons */}
                  {selectedContent.type === 'bookmark' && selectedContent.url && (
                    <button
                      onClick={() => window.open(selectedContent.url, '_blank')}
                      className="p-2 rounded-lg hover:bg-accent transition-colors"
                      title="Open URL"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={handleExport}
                    className="p-2 rounded-lg hover:bg-accent transition-colors"
                    title="Export/Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 rounded-lg hover:bg-accent transition-colors"
                    title="Share"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>

                  {/* More Actions Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setShowContentMenu(!showContentMenu)}
                      className="p-2 rounded-lg hover:bg-accent transition-colors"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                    {showContentMenu && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowContentMenu(false)} />
                        <div className="absolute right-0 top-full mt-1 w-48 rounded-xl bg-card border border-border shadow-lg z-50 py-1">
                          {selectedContent.state !== 'indexed' && (
                            <button
                              onClick={() => { handleReimport(); setShowContentMenu(false) }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors text-left"
                            >
                              <RefreshCw className="w-4 h-4 text-orange-500" />
                              <span className="text-sm">Reimport</span>
                            </button>
                          )}
                          <button
                            onClick={() => { openRenameContentDialog(selectedContent); setShowContentMenu(false) }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors text-left"
                          >
                            <Pencil className="w-4 h-4 text-indigo-500" />
                            <span className="text-sm">Rename</span>
                          </button>
                          <button
                            onClick={() => { openMoveContentDialog(selectedContent); setShowContentMenu(false) }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors text-left"
                          >
                            <FolderInput className="w-4 h-4 text-amber-500" />
                            <span className="text-sm">Move to Space</span>
                          </button>
                          <button
                            onClick={() => { openContentInfo(); setShowContentMenu(false) }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors text-left"
                          >
                            <Info className="w-4 h-4 text-cyan-500" />
                            <span className="text-sm">Content Info</span>
                          </button>
                          <div className="my-1 border-t border-border" />
                          <button
                            onClick={() => { openDeleteContentDialog(selectedContent); setShowContentMenu(false) }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors text-left text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-sm">Delete</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {/* Content Viewer */}
              <ContentViewer content={selectedContent} contentId={selectedContentId} viewMode={contentViewMode} />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">Select a document to view</p>
              </div>
            </div>
          )}
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="w-96 border-l border-border flex flex-col bg-card/50">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="font-medium text-sm">AI Chat</span>
                {threadId && (
                  <span className="text-xs text-muted-foreground">• Active</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setShowChatHistory(!showChatHistory); if (!showChatHistory) loadChatHistory() }}
                  className={`p-1.5 rounded-lg transition-colors ${showChatHistory ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                  title="Chat History"
                >
                  <History className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setThreadId(null); setMessages([]) }}
                  className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                  title="New Chat"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowChat(false)}
                  className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Chat History Panel */}
            {showChatHistory && (
              <div className="border-b border-border bg-muted/30 max-h-64 overflow-auto">
                <div className="p-2">
                  <div className="flex items-center justify-between px-2 py-1 mb-1">
                    <span className="text-xs font-medium text-muted-foreground">Recent Chats</span>
                    {chatHistoryLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                  </div>
                  {chatThreads.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No chat history</p>
                  ) : (
                    <div className="space-y-1">
                      {chatThreads.map(thread => (
                        <div
                          key={thread.id}
                          className={`group flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                            threadId === thread.id ? 'bg-primary/10' : 'hover:bg-accent'
                          }`}
                          onClick={() => loadThread(thread)}
                        >
                          <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate flex-1">{thread.name || 'Untitled Chat'}</span>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                const newName = prompt('Rename chat:', thread.name)
                                if (newName) handleRenameThread(thread.id, newName)
                              }}
                              className="p-1 rounded hover:bg-background/80"
                              title="Rename"
                            >
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (confirm('Delete this chat?')) handleDeleteThread(thread.id)
                              }}
                              className="p-1 rounded hover:bg-background/80"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attached Contents */}
            {(selectedContent || attachedContents.length > 0) && (
              <AttachedContents
                contents={[
                  ...(selectedContent ? [selectedContent] : []),
                  ...attachedContents.filter(c => (c.content_id || c.id) !== selectedContentId)
                ]}
                onRemove={(content) => {
                  const id = content.content_id || content.id
                  if (id === selectedContentId) return // Can't remove selected content
                  handleRemoveContent(content)
                }}
                onAdd={() => setShowContentPicker(true)}
              />
            )}
            
            {/* Chat Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Ask questions about<br />
                    <span className="font-medium text-foreground">{selectedContent?.title || 'your content'}</span>
                  </p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      msg.role === 'user' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content || '...'}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))
              )}
              {chatLoading && messages[messages.length - 1]?.role === 'assistant' && !messages[messages.length - 1]?.content && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <button
                  onClick={() => setShowContentPicker(true)}
                  className={`p-2 rounded-full transition-colors ${
                    attachedContents.length > 0
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                  }`}
                  title="Attach content"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Ask about attached content..."
                  className="flex-1 px-4 py-2 rounded-full bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  disabled={chatLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={chatLoading || !inputMessage.trim()}
                  className="p-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Picker Modal */}
        <ContentPickerModal
          isOpen={showContentPicker}
          onClose={() => setShowContentPicker(false)}
          onSelect={handleContentSelect}
          selectedIds={[
            ...(selectedContentId ? [selectedContentId] : []),
            ...attachedContents.map(c => c.content_id || c.id)
          ]}
        />

        {/* Import Content Modal */}
        <ImportContentModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={loadTree}
          defaultSpaceId={rootSpaceId}
        />

        {/* Bulk Import Modal (Premium) */}
        <BulkImportModal
          isOpen={showBulkImportModal}
          onClose={() => setShowBulkImportModal(false)}
          onSuccess={loadTree}
          defaultSpaceId={rootSpaceId}
        />

        {/* Rename Dialog */}
        <RenameDialog
          isOpen={renameDialog.open}
          onClose={() => setRenameDialog({ open: false, type: null, id: null, name: '' })}
          onSubmit={renameDialog.type === 'content' ? handleRenameContent : handleRenameSpace}
          initialValue={renameDialog.name}
          title={renameDialog.type === 'content' ? 'Rename Content' : 'Rename Space'}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, type: null, id: null, name: '' })}
          onConfirm={deleteDialog.type === 'content' ? handleDeleteContent : handleDeleteSpace}
          title={`Delete ${deleteDialog.type === 'content' ? 'Content' : 'Space'}`}
          message={`Are you sure you want to delete "${deleteDialog.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          destructive
        />

        {/* Move to Space Dialog */}
        <MoveToSpaceDialog
          isOpen={moveDialog.open}
          onClose={() => setMoveDialog({ open: false, contentId: null, currentSpaceId: null })}
          onSubmit={handleMoveContent}
          spaces={allSpaces}
          currentSpaceId={moveDialog.currentSpaceId}
        />

        {/* Create Space Dialog */}
        <CreateSpaceDialog
          isOpen={createSpaceDialog.open}
          onClose={() => setCreateSpaceDialog({ open: false, parentId: null })}
          onSubmit={handleCreateSpace}
          parentId={createSpaceDialog.parentId}
        />

        {/* Content Info Dialog */}
        {contentInfoDialog.open && contentInfoDialog.content && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => setContentInfoDialog({ open: false, content: null })} />
            <div className="relative bg-card rounded-xl border border-border shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-auto">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold">Content Info</h3>
                <button onClick={() => setContentInfoDialog({ open: false, content: null })} className="p-1 rounded hover:bg-accent">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">Title</label>
                  <p className="text-sm font-medium">{contentInfoDialog.content.title}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Type</label>
                  <p className="text-sm">{contentTypeConfig[contentInfoDialog.content.type]?.label || contentInfoDialog.content.type}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Status</label>
                  <p className="text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                      contentInfoDialog.content.state === 'indexed' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {contentInfoDialog.content.state || 'processing'}
                    </span>
                  </p>
                </div>
                {contentInfoDialog.content.url && (
                  <div>
                    <label className="text-xs text-muted-foreground">URL</label>
                    <p className="text-sm text-primary truncate">{contentInfoDialog.content.url}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-muted-foreground">Created</label>
                  <p className="text-sm">{formatDate(contentInfoDialog.content.created_at || contentInfoDialog.content.createdAt)}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Updated</label>
                  <p className="text-sm">{formatDate(contentInfoDialog.content.updated_at || contentInfoDialog.content.updatedAt)}</p>
                </div>
                {contentInfoDialog.content.description && (
                  <div>
                    <label className="text-xs text-muted-foreground">Description</label>
                    <p className="text-sm">{contentInfoDialog.content.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Tree View Component
function TreeView({ 
  spaces, 
  contents, 
  expandedSpaces, 
  spaceContents, 
  selectedContentId, 
  onToggleSpace, 
  onSelectContent,
  onRenameSpace,
  onDeleteSpace,
  onCreateSpace,
  onRenameContent,
  onDeleteContent,
  onMoveContent,
}) {
  return (
    <div className="space-y-1">
      {/* Root Spaces */}
      {spaces.map(space => (
        <SpaceNode
          key={space.id}
          space={space}
          expanded={expandedSpaces[space.id]}
          spaceContents={spaceContents}
          selectedContentId={selectedContentId}
          onToggle={() => onToggleSpace(space.id)}
          onSelectContent={onSelectContent}
          expandedSpaces={expandedSpaces}
          onToggleSpace={onToggleSpace}
          onRenameSpace={onRenameSpace}
          onDeleteSpace={onDeleteSpace}
          onCreateSpace={onCreateSpace}
          onRenameContent={onRenameContent}
          onDeleteContent={onDeleteContent}
          onMoveContent={onMoveContent}
        />
      ))}
      
      {/* Root Contents */}
      {contents.map(content => (
        <ContentNode
          key={content.content_id || content.id}
          content={content}
          selected={selectedContentId === (content.content_id || content.id)}
          onSelect={() => onSelectContent(content.content_id || content.id)}
          onRename={() => onRenameContent(content)}
          onDelete={() => onDeleteContent(content)}
          onMove={() => onMoveContent(content)}
        />
      ))}
    </div>
  )
}

function SpaceNode({ 
  space, 
  expanded, 
  spaceContents, 
  selectedContentId, 
  onToggle, 
  onSelectContent, 
  expandedSpaces, 
  onToggleSpace,
  onRenameSpace,
  onDeleteSpace,
  onCreateSpace,
  onRenameContent,
  onDeleteContent,
  onMoveContent,
}) {
  const children = spaceContents[space.id]
  
  return (
    <div>
      <div className="group flex items-center gap-1 hover:bg-accent rounded-lg transition-colors">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 px-2 py-1.5 text-left"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
          {expanded ? (
            <FolderOpen className="w-4 h-4 text-amber-500" />
          ) : (
            <Folder className="w-4 h-4 text-amber-500" />
          )}
          <span className="text-sm truncate flex-1">{space.name}</span>
        </button>
        <div className="flex items-center gap-0.5 pr-1">
          <button
            onClick={(e) => { e.stopPropagation(); onCreateSpace(space.id) }}
            className="p-1 rounded hover:bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Add sub-space"
          >
            <FolderPlus className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRenameSpace(space) }}
            className="p-1 rounded hover:bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Rename"
          >
            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDeleteSpace(space) }}
            className="p-1 rounded hover:bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5 text-destructive" />
          </button>
        </div>
      </div>
      
      {expanded && children && (
        <div className="ml-4 pl-2 border-l border-border">
          {children.spaces?.map(childSpace => (
            <SpaceNode
              key={childSpace.id}
              space={childSpace}
              expanded={expandedSpaces[childSpace.id]}
              spaceContents={spaceContents}
              selectedContentId={selectedContentId}
              onToggle={() => onToggleSpace(childSpace.id)}
              onSelectContent={onSelectContent}
              expandedSpaces={expandedSpaces}
              onToggleSpace={onToggleSpace}
              onRenameSpace={onRenameSpace}
              onDeleteSpace={onDeleteSpace}
              onCreateSpace={onCreateSpace}
              onRenameContent={onRenameContent}
              onDeleteContent={onDeleteContent}
              onMoveContent={onMoveContent}
            />
          ))}
          {children.contents?.map(content => (
            <ContentNode
              key={content.content_id || content.id}
              content={content}
              selected={selectedContentId === (content.content_id || content.id)}
              onSelect={() => onSelectContent(content.content_id || content.id)}
              onRename={() => onRenameContent(content)}
              onDelete={() => onDeleteContent(content)}
              onMove={() => onMoveContent(content)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ContentNode({ content, selected, onSelect, onRename, onDelete, onMove }) {
  const config = contentTypeConfig[content.type] || contentTypeConfig.unknown
  const Icon = iconMap[content.type] || File
  
  return (
    <div 
      className={`group flex items-center gap-1 rounded-lg transition-colors ${
        selected ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
      }`}
    >
      <button
        onClick={onSelect}
        className="flex-1 flex items-center gap-2 px-2 py-1.5 text-left"
      >
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: config.color }} />
        <span className="text-sm truncate">{content.title}</span>
      </button>
      <div className="flex items-center gap-0.5 pr-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onMove() }}
          className="p-1 rounded hover:bg-background/80"
          title="Move to space"
        >
          <FolderInput className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRename() }}
          className="p-1 rounded hover:bg-background/80"
          title="Rename"
        >
          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete() }}
          className="p-1 rounded hover:bg-background/80"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5 text-destructive" />
        </button>
      </div>
    </div>
  )
}

// Content Viewer Component
function ContentViewer({ content, contentId, viewMode = 'view' }) {
  const type = content.type?.toLowerCase()

  // Listen mode - Text-to-Speech
  if (viewMode === 'listen') {
    return <ListenViewer content={content} />
  }

  // Markdown mode - Show raw markdown with editor
  if (viewMode === 'markdown') {
    return <MarkdownEditor content={content} contentId={contentId} isNote={type === 'note'} />
  }

  // View mode - Default viewers based on type
  switch (type) {
    case 'note':
      return <NoteEditor content={content} contentId={contentId} />
    case 'pdf':
      return <PDFViewer content={content} contentId={contentId} />
    case 'bookmark':
      return <BookmarkViewer content={content} />
    case 'image':
      return <ImageViewer content={content} />
    case 'docx':
    case 'doc':
      return <DocumentViewer content={content} contentId={contentId} type="word" />
    case 'pptx':
    case 'ppt':
      return <DocumentViewer content={content} contentId={contentId} type="powerpoint" />
    case 'xlsx':
    case 'xls':
      return <DocumentViewer content={content} contentId={contentId} type="excel" />
    case 'epub':
      return <EpubViewer content={content} contentId={contentId} />
    default:
      return <TextViewer content={content} />
  }
}

function PDFViewer({ content, contentId }) {
  const [pdfUrl, setPdfUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadPdf()
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    }
  }, [contentId])

  const loadPdf = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getIdToken()
      const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'
      const response = await fetch(`${API_BASE}/content/${contentId}?raw=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!response.ok) throw new Error(`Failed to load PDF: ${response.status}`)
      const arrayBuffer = await response.arrayBuffer()
      const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' })
      setPdfUrl(URL.createObjectURL(pdfBlob))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <iframe src={pdfUrl} className="flex-1 w-full border-0" title={content.title} />
  )
}

function BookmarkViewer({ content }) {
  const [copied, setCopied] = useState(false)
  const [showSaveAs, setShowSaveAs] = useState(false)

  const text = content.content_text || content.summary || content.description || ''

  if (!content.url) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-muted-foreground">No URL available</p>
      </div>
    )
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(content.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSaveAsNote = async (title, noteContent) => {
    await createContent({
      title,
      content: noteContent,
      type: 'note',
      spaceId: content.space_id || '',
    })
  }

  // Get hostname safely
  let hostname = ''
  try {
    hostname = new URL(content.url).hostname
  } catch (e) {
    hostname = content.url
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-teal-500" />
          <span className="text-sm font-medium">Bookmark</span>
        </div>
        {text && (
          <button
            onClick={() => setShowSaveAs(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            title="Save as new note"
          >
            <FilePlus2 className="w-3.5 h-3.5" />
            Save As Note
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-auto">
        {/* Preview image banner if available */}
        {(content.og_image || content.image) && (
          <div className="w-full h-48 sm:h-64 bg-muted overflow-hidden flex-shrink-0">
            <img
              src={content.og_image || content.image}
              alt={content.title}
              className="w-full h-full object-cover"
              onError={(e) => e.target.parentElement.style.display = 'none'}
            />
          </div>
        )}

        <div className="flex-1 p-6 sm:p-8">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold">{content.title}</h1>

            {/* URL with favicon */}
            <div className="flex items-center gap-3">
              <img
                src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
                alt=""
                className="w-5 h-5 rounded"
                onError={(e) => e.target.style.display = 'none'}
              />
              <a
                href={content.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline truncate max-w-md"
              >
                {hostname}
              </a>
            </div>

            {/* Description */}
            {(content.description || content.og_description || content.summary) && (
              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-muted-foreground leading-relaxed">
                  {content.summary || content.description || content.og_description}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              <a
                href={content.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium shadow-lg shadow-primary/25"
              >
                <ExternalLink className="w-4 h-4" />
                Open Website
              </a>
              <button
                onClick={handleCopyUrl}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border hover:bg-accent transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy URL
                  </>
                )}
              </button>
            </div>

            {/* Content text if scraped/available */}
            {content.content_text && (
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-4">Saved Content</h3>
                <div className="prose dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content.content_text}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save As Note Dialog */}
      <SaveAsNoteDialog
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsNote}
        defaultTitle={content.title}
        content={text}
      />
    </div>
  )
}

function ImageViewer({ content }) {
  const imageUrl = content.url || content.og_image || content.image

  return (
    <div className="flex-1 flex items-center justify-center p-8 bg-black/5 dark:bg-white/5">
      {imageUrl ? (
        <img src={imageUrl} alt={content.title} className="max-w-full max-h-full object-contain rounded-lg" />
      ) : (
        <p className="text-muted-foreground">No image available</p>
      )}
    </div>
  )
}

function TextViewer({ content }) {
  const text = content.content_text || content.summary || content.description || ''
  const [showSaveAs, setShowSaveAs] = useState(false)

  const handleSaveAsNote = async (title, noteContent) => {
    await createContent({
      title,
      content: noteContent,
      type: 'note',
      spaceId: content.space_id || '',
    })
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <File className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Content Viewer</span>
        </div>
        <button
          onClick={() => setShowSaveAs(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          title="Save as new note"
        >
          <FilePlus2 className="w-3.5 h-3.5" />
          Save As Note
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          {content.summary && (
            <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
              <h3 className="text-sm font-medium text-primary mb-2">AI Summary</h3>
              <p className="text-sm">{content.summary}</p>
            </div>
          )}
          {text ? (
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-muted-foreground">No content available</p>
          )}
        </div>
      </div>

      {/* Save As Note Dialog */}
      <SaveAsNoteDialog
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsNote}
        defaultTitle={content.title}
        content={text}
      />
    </div>
  )
}

// Listen Mode - Enhanced Text-to-Speech Viewer with sentence tracking
function ListenViewer({ content }) {
  const text = content.content_text || content.summary || content.description || ''
  const contentId = content.id || content.content_id

  // Parse text into sentences
  const parseSentences = (text) => {
    if (!text) return []
    // Clean up the text - remove markdown formatting, extra whitespace
    const cleanText = text
      .replace(/#{1,6}\s+/g, '') // Remove markdown headers
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
      .replace(/\*([^*]+)\*/g, '$1') // Remove italic
      .replace(/`([^`]+)`/g, '$1') // Remove code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim()

    // Split by sentence-ending punctuation (supports multiple languages)
    const sentencePattern = /[^.!?。！？；।॥۔؛؟ฯ]+[.!?。！？；।॥۔؛؟ฯ]+/g
    const matches = cleanText.match(sentencePattern)

    if (!matches) {
      // If no sentence endings found, split by double newlines or return as single chunk
      return cleanText.split(/\n\n+/).filter(s => s.trim().length > 0)
    }

    return matches
      .map(s => s.trim())
      .filter(s => s.length > 0 && s.length > 2) // Filter out very short matches
  }

  const sentences = parseSentences(text)

  // State
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(() => {
    // Restore saved position
    const saved = localStorage.getItem(`tts_position_${contentId}`)
    return saved ? Math.min(parseInt(saved, 10), sentences.length - 1) : 0
  })
  const [speechRate, setSpeechRate] = useState(() => {
    const saved = localStorage.getItem('tts_rate')
    return saved ? parseFloat(saved) : 1.0
  })
  const [voices, setVoices] = useState([])
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('tts_language') || ''
  })
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem('tts_voice') || ''
  })
  const [showSettings, setShowSettings] = useState(false)

  // Language code to country name and flag mapping
  const languageInfo = {
    'en-US': { name: 'English (US)', flag: '🇺🇸' },
    'en-GB': { name: 'English (UK)', flag: '🇬🇧' },
    'en-AU': { name: 'English (Australia)', flag: '🇦🇺' },
    'en-IN': { name: 'English (India)', flag: '🇮🇳' },
    'en-IE': { name: 'English (Ireland)', flag: '🇮🇪' },
    'en-ZA': { name: 'English (South Africa)', flag: '🇿🇦' },
    'en-NZ': { name: 'English (New Zealand)', flag: '🇳🇿' },
    'zh-CN': { name: 'Chinese (Simplified)', flag: '🇨🇳' },
    'zh-TW': { name: 'Chinese (Traditional)', flag: '🇹🇼' },
    'zh-HK': { name: 'Chinese (Hong Kong)', flag: '🇭🇰' },
    'ja-JP': { name: 'Japanese', flag: '🇯🇵' },
    'ko-KR': { name: 'Korean', flag: '🇰🇷' },
    'es-ES': { name: 'Spanish (Spain)', flag: '🇪🇸' },
    'es-MX': { name: 'Spanish (Mexico)', flag: '🇲🇽' },
    'es-AR': { name: 'Spanish (Argentina)', flag: '🇦🇷' },
    'es-US': { name: 'Spanish (US)', flag: '🇺🇸' },
    'fr-FR': { name: 'French (France)', flag: '🇫🇷' },
    'fr-CA': { name: 'French (Canada)', flag: '🇨🇦' },
    'de-DE': { name: 'German', flag: '🇩🇪' },
    'de-AT': { name: 'German (Austria)', flag: '🇦🇹' },
    'de-CH': { name: 'German (Switzerland)', flag: '🇨🇭' },
    'it-IT': { name: 'Italian', flag: '🇮🇹' },
    'pt-BR': { name: 'Portuguese (Brazil)', flag: '🇧🇷' },
    'pt-PT': { name: 'Portuguese (Portugal)', flag: '🇵🇹' },
    'ru-RU': { name: 'Russian', flag: '🇷🇺' },
    'ar-SA': { name: 'Arabic (Saudi Arabia)', flag: '🇸🇦' },
    'ar-AE': { name: 'Arabic (UAE)', flag: '🇦🇪' },
    'hi-IN': { name: 'Hindi', flag: '🇮🇳' },
    'th-TH': { name: 'Thai', flag: '🇹🇭' },
    'vi-VN': { name: 'Vietnamese', flag: '🇻🇳' },
    'id-ID': { name: 'Indonesian', flag: '🇮🇩' },
    'ms-MY': { name: 'Malay', flag: '🇲🇾' },
    'nl-NL': { name: 'Dutch', flag: '🇳🇱' },
    'nl-BE': { name: 'Dutch (Belgium)', flag: '🇧🇪' },
    'pl-PL': { name: 'Polish', flag: '🇵🇱' },
    'tr-TR': { name: 'Turkish', flag: '🇹🇷' },
    'sv-SE': { name: 'Swedish', flag: '🇸🇪' },
    'da-DK': { name: 'Danish', flag: '🇩🇰' },
    'nb-NO': { name: 'Norwegian', flag: '🇳🇴' },
    'no-NO': { name: 'Norwegian', flag: '🇳🇴' },
    'fi-FI': { name: 'Finnish', flag: '🇫🇮' },
    'el-GR': { name: 'Greek', flag: '🇬🇷' },
    'he-IL': { name: 'Hebrew', flag: '🇮🇱' },
    'cs-CZ': { name: 'Czech', flag: '🇨🇿' },
    'sk-SK': { name: 'Slovak', flag: '🇸🇰' },
    'hu-HU': { name: 'Hungarian', flag: '🇭🇺' },
    'ro-RO': { name: 'Romanian', flag: '🇷🇴' },
    'uk-UA': { name: 'Ukrainian', flag: '🇺🇦' },
    'bg-BG': { name: 'Bulgarian', flag: '🇧🇬' },
    'hr-HR': { name: 'Croatian', flag: '🇭🇷' },
    'ca-ES': { name: 'Catalan', flag: '🇪🇸' },
  }

  // Get display name for a language code
  const getLanguageDisplay = (langCode) => {
    const info = languageInfo[langCode]
    if (info) {
      return `${info.flag} ${info.name}`
    }
    // Fallback: try to parse the language code
    const flag = getFlagFromCode(langCode)
    return `${flag} ${langCode}`
  }

  // Get flag emoji from language code (fallback)
  const getFlagFromCode = (langCode) => {
    const parts = langCode.split('-')
    if (parts.length >= 2) {
      const countryCode = parts[1].toUpperCase()
      // Convert country code to flag emoji
      const flagOffset = 127397
      try {
        return String.fromCodePoint(...[...countryCode].map(c => c.charCodeAt(0) + flagOffset))
      } catch {
        return '🌐'
      }
    }
    return '🌐'
  }

  // Get unique languages from voices
  const languages = [...new Set(voices.map(v => v.lang))].sort((a, b) => {
    // Sort by display name
    const nameA = languageInfo[a]?.name || a
    const nameB = languageInfo[b]?.name || b
    return nameA.localeCompare(nameB)
  })

  // Filter voices by selected language
  const filteredVoices = selectedLanguage
    ? voices.filter(v => v.lang === selectedLanguage)
    : voices

  const sentenceRefs = useRef([])
  const scrollContainerRef = useRef(null)
  const synthRef = useRef(window.speechSynthesis)
  const utteranceRef = useRef(null)

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = synthRef.current.getVoices()
      setVoices(availableVoices)
      // Set default voice if none selected
      if (!selectedVoice && availableVoices.length > 0) {
        const defaultVoice = availableVoices.find(v => v.default) || availableVoices[0]
        setSelectedVoice(defaultVoice.name)
      }
    }

    loadVoices()
    // Chrome loads voices async
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices
    }

    return () => {
      synthRef.current.cancel()
    }
  }, [])

  // Save position when it changes
  useEffect(() => {
    if (contentId) {
      localStorage.setItem(`tts_position_${contentId}`, currentIndex.toString())
    }
  }, [currentIndex, contentId])

  // Save settings when they change
  useEffect(() => {
    localStorage.setItem('tts_rate', speechRate.toString())
  }, [speechRate])

  useEffect(() => {
    if (selectedVoice) {
      localStorage.setItem('tts_voice', selectedVoice)
    }
  }, [selectedVoice])

  useEffect(() => {
    localStorage.setItem('tts_language', selectedLanguage)
    // When language changes, select the first voice in that language if current voice doesn't match
    if (selectedLanguage && selectedVoice) {
      const currentVoice = voices.find(v => v.name === selectedVoice)
      if (currentVoice && currentVoice.lang !== selectedLanguage) {
        const firstVoiceInLang = voices.find(v => v.lang === selectedLanguage)
        if (firstVoiceInLang) {
          setSelectedVoice(firstVoiceInLang.name)
        }
      }
    }
  }, [selectedLanguage, voices])

  // Scroll to current sentence
  useEffect(() => {
    if (sentenceRefs.current[currentIndex] && scrollContainerRef.current) {
      sentenceRefs.current[currentIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [currentIndex])

  // Speak current sentence
  const speakSentence = (index) => {
    if (index >= sentences.length) {
      setIsPlaying(false)
      return
    }

    const synth = synthRef.current
    synth.cancel() // Cancel any ongoing speech

    const utterance = new SpeechSynthesisUtterance(sentences[index])
    utterance.rate = speechRate

    // Find and set the selected voice
    const voice = voices.find(v => v.name === selectedVoice)
    if (voice) {
      utterance.voice = voice
    }

    utterance.onend = () => {
      // Auto-advance to next sentence
      if (index < sentences.length - 1) {
        setCurrentIndex(index + 1)
        speakSentence(index + 1)
      } else {
        setIsPlaying(false)
      }
    }

    utterance.onerror = (e) => {
      console.error('Speech error:', e)
      setIsPlaying(false)
    }

    utteranceRef.current = utterance
    synth.speak(utterance)
    setIsPlaying(true)
    setCurrentIndex(index)
  }

  const togglePlay = () => {
    const synth = synthRef.current
    if (isPlaying) {
      synth.pause()
      setIsPlaying(false)
    } else {
      if (synth.paused) {
        synth.resume()
        setIsPlaying(true)
      } else {
        speakSentence(currentIndex)
      }
    }
  }

  const stop = () => {
    synthRef.current.cancel()
    setIsPlaying(false)
  }

  const skipPrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1)
    setCurrentIndex(newIndex)
    if (isPlaying) {
      speakSentence(newIndex)
    }
  }

  const skipNext = () => {
    const newIndex = Math.min(sentences.length - 1, currentIndex + 1)
    setCurrentIndex(newIndex)
    if (isPlaying) {
      speakSentence(newIndex)
    }
  }

  const jumpToSentence = (index) => {
    setCurrentIndex(index)
    if (isPlaying) {
      speakSentence(index)
    }
  }

  const resetPosition = () => {
    stop()
    setCurrentIndex(0)
  }

  const progress = sentences.length > 0 ? ((currentIndex + 1) / sentences.length) * 100 : 0

  if (!text || sentences.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="text-center max-w-md">
          <Volume2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No text available for reading</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header with title and settings */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center ${isPlaying ? 'animate-pulse' : ''}`}>
            <Volume2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-sm line-clamp-1">{content.title}</h2>
            <p className="text-xs text-muted-foreground">
              {currentIndex + 1} / {sentences.length} sentences • {text.split(' ').length} words
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetPosition}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Reset to beginning"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Playback controls - at the top */}
      <div className="px-4 py-3 border-b border-border/50 bg-card/50">
        <div className="flex items-center justify-center gap-3">
          {/* Skip backward */}
          <button
            onClick={skipPrevious}
            disabled={currentIndex === 0}
            className="p-2.5 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous sentence"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="p-3.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all active:scale-95"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" fill="currentColor" />
            ) : (
              <Play className="w-6 h-6" fill="currentColor" />
            )}
          </button>

          {/* Skip forward */}
          <button
            onClick={skipNext}
            disabled={currentIndex === sentences.length - 1}
            className="p-2.5 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next sentence"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Stop */}
          {isPlaying && (
            <button
              onClick={stop}
              className="p-2.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors"
              title="Stop"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="px-4 py-3 border-b border-border bg-muted/20 space-y-4">
          {/* Speed control */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-muted-foreground">Speed</label>
              <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">{speechRate.toFixed(1)}x</span>
            </div>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={speechRate}
              onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0.5x</span>
              <span>1.0x</span>
              <span>2.0x</span>
            </div>
          </div>

          {/* Language selection */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-2">Language</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">🌐 All Languages</option>
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {getLanguageDisplay(lang)}
                </option>
              ))}
            </select>
          </div>

          {/* Voice selection */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-2">
              Voice {selectedLanguage && `(${filteredVoices.length} available)`}
            </label>
            <select
              value={selectedVoice}
              onChange={(e) => setSelectedVoice(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {filteredVoices.map((voice) => (
                <option key={voice.name} value={voice.name}>
                  {voice.name} {!selectedLanguage && `(${voice.lang})`}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="px-4 py-2 border-b border-border bg-background">
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-muted-foreground w-8">{currentIndex + 1}</span>
          <div
            className="flex-1 h-2 bg-muted rounded-full cursor-pointer relative overflow-hidden"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const x = e.clientX - rect.left
              const percent = x / rect.width
              const newIndex = Math.round(percent * (sentences.length - 1))
              jumpToSentence(newIndex)
            }}
          >
            <div
              className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-150"
              style={{ width: `${progress}%` }}
            />
            {/* Position indicator */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm transition-all duration-150"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>
          <span className="text-xs font-mono text-muted-foreground w-8 text-right">{sentences.length}</span>
        </div>
      </div>

      {/* Sentence display with highlighting */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
      >
        {sentences.map((sentence, index) => {
          const isCurrent = index === currentIndex
          const isPast = index < currentIndex

          return (
            <div
              key={index}
              ref={(el) => sentenceRefs.current[index] = el}
              onClick={() => jumpToSentence(index)}
              className={`
                p-3 rounded-lg cursor-pointer transition-all duration-200
                border-l-4
                ${isCurrent
                  ? 'bg-primary/10 border-l-primary shadow-sm'
                  : isPast
                    ? 'border-l-primary/30 opacity-60 hover:opacity-80'
                    : 'border-l-transparent hover:bg-muted/50'
                }
              `}
            >
              <p className={`
                text-sm leading-relaxed
                ${isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground'}
              `}>
                {sentence}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Note Editor Component - Rich text editor for notes
function NoteEditor({ content, contentId }) {
  const [text, setText] = useState(content.content_text || '')
  const [isSaving, setIsSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [saveStatus, setSaveStatus] = useState('')
  const [showSaveAs, setShowSaveAs] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    setSaveStatus('')
    try {
      const { updateContent } = await import('../lib/api')
      await updateContent({ contentId, content: text })
      setSaveStatus('Saved')
      setTimeout(() => setSaveStatus(''), 2000)
    } catch (err) {
      console.error('Save error:', err)
      setSaveStatus('Error saving')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAsNote = async (title, noteContent) => {
    await createContent({
      title,
      content: noteContent,
      type: 'note',
      spaceId: content.space_id || '',
    })
  }

  // Auto-save on blur
  const handleBlur = () => {
    if (isEditing && text !== content.content_text) {
      handleSave()
    }
    setIsEditing(false)
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Note Editor</span>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus && (
            <span className={`text-xs ${saveStatus === 'Saved' ? 'text-green-500' : 'text-destructive'}`}>
              {saveStatus}
            </span>
          )}
          <button
            onClick={() => setShowSaveAs(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            title="Save as new note"
          >
            <FilePlus2 className="w-3.5 h-3.5" />
            Save As
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      {/* Editor Area */}
      <div className="flex-1 overflow-auto">
        {isEditing ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={handleBlur}
            autoFocus
            className="w-full h-full p-6 bg-background text-sm resize-none focus:outline-none"
            placeholder="Start writing your note..."
          />
        ) : (
          <div
            onClick={() => setIsEditing(true)}
            className="w-full h-full p-6 cursor-text"
          >
            {text ? (
              <div className="prose dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-muted-foreground italic">Click to start writing...</p>
            )}
          </div>
        )}
      </div>

      {/* Save As Note Dialog */}
      <SaveAsNoteDialog
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsNote}
        defaultTitle={content.title}
        content={text}
      />
    </div>
  )
}

// Markdown Editor Component
function MarkdownEditor({ content, contentId, isNote = false }) {
  const [text, setText] = useState(content.content_text || content.summary || '')
  const [isSaving, setIsSaving] = useState(false)
  const [showSaveAs, setShowSaveAs] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Save content via API
      const { updateContent } = await import('../lib/api')
      await updateContent({ contentId, content: text })
    } catch (err) {
      console.error('Save error:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAsNote = async (title, noteContent) => {
    await createContent({
      title,
      content: noteContent,
      type: 'note',
      spaceId: content.space_id || '',
    })
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Markdown {isNote ? 'Editor' : 'Viewer'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSaveAs(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            title="Save as new note"
          >
            <FilePlus2 className="w-3.5 h-3.5" />
            Save As Note
          </button>
          {isNote && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          )}
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="flex-1 p-4 bg-background text-sm font-mono resize-none focus:outline-none"
        placeholder={isNote ? "Write your markdown here..." : "View markdown content..."}
        readOnly={!isNote}
      />

      {/* Save As Note Dialog */}
      <SaveAsNoteDialog
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsNote}
        defaultTitle={content.title}
        content={text}
      />
    </div>
  )
}

// Document Viewer (Word, PowerPoint, Excel)
function DocumentViewer({ content, contentId, type }) {
  const text = content.content_text || content.summary || ''
  const [showSaveAs, setShowSaveAs] = useState(false)

  // For Office documents, show extracted text with type indicator
  const typeLabels = {
    word: 'Word Document',
    powerpoint: 'PowerPoint Presentation',
    excel: 'Excel Spreadsheet',
  }

  const handleSaveAsNote = async (title, noteContent) => {
    await createContent({
      title,
      content: noteContent,
      type: 'note',
      spaceId: content.space_id || '',
    })
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium">{typeLabels[type] || 'Document'}</span>
        </div>
        <button
          onClick={() => setShowSaveAs(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          title="Save as new note"
        >
          <FilePlus2 className="w-3.5 h-3.5" />
          Save As Note
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          {content.summary && (
            <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
              <h3 className="text-sm font-medium text-primary mb-2">AI Summary</h3>
              <p className="text-sm">{content.summary}</p>
            </div>
          )}
          {text ? (
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Document content is being processed...</p>
            </div>
          )}
        </div>
      </div>

      {/* Save As Note Dialog */}
      <SaveAsNoteDialog
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsNote}
        defaultTitle={content.title}
        content={text}
      />
    </div>
  )
}

// EPUB Viewer
function EpubViewer({ content, contentId }) {
  const text = content.content_text || content.summary || ''
  const [showSaveAs, setShowSaveAs] = useState(false)

  const handleSaveAsNote = async (title, noteContent) => {
    await createContent({
      title,
      content: noteContent,
      type: 'note',
      spaceId: content.space_id || '',
    })
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium">eBook Viewer</span>
        </div>
        <button
          onClick={() => setShowSaveAs(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          title="Save as new note"
        >
          <FilePlus2 className="w-3.5 h-3.5" />
          Save As Note
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          {content.summary && (
            <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
              <h3 className="text-sm font-medium text-primary mb-2">AI Summary</h3>
              <p className="text-sm">{content.summary}</p>
            </div>
          )}
          {text ? (
            <div className="prose dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">eBook content is being processed...</p>
            </div>
          )}
        </div>
      </div>

      {/* Save As Note Dialog */}
      <SaveAsNoteDialog
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsNote}
        defaultTitle={content.title}
        content={text}
      />
    </div>
  )
}

// Save As Note Dialog
function SaveAsNoteDialog({ isOpen, onClose, onSave, defaultTitle = '', content = '' }) {
  const [title, setTitle] = useState(defaultTitle)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle ? `${defaultTitle} (Copy)` : 'New Note')
      setError('')
      setTimeout(() => inputRef.current?.select(), 50)
    }
  }, [isOpen, defaultTitle])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Please enter a title')
      return
    }
    setLoading(true)
    setError('')
    try {
      await onSave(title.trim(), content)
      onClose()
    } catch (err) {
      console.error('Save as note error:', err)
      setError(err.message || 'Failed to save note')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] bg-card rounded-xl shadow-2xl z-50 p-6 animate-scale-in">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <FilePlus2 className="w-5 h-5 text-primary" />
          </div>
          <h3 className="text-lg font-semibold">Save As New Note</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Create a new note with this content. The original content will remain unchanged.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none"
            placeholder="Enter note title..."
            autoFocus
          />
          {error && (
            <p className="text-sm text-destructive mt-2">{error}</p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Save as Note
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
